import { useEffect, useState } from "react";
import { UserPlus, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PageContainer from "@/components/PageContainer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const ROLE_BADGE = {
  admin: "bg-[#EFDCC5] text-[#7A4A28] border-[#A06E4D]",
  pegawai: "bg-[#FBEAD6] text-[#B87148] border-[#D38B60]",
  pengguna: "bg-[#F7E5D2] text-[#8A5230] border-[#E0A988]",
};

const EMPTY_FORM = { username: "", name: "", password: "", role: "pegawai" };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (e) {
      toast.error("Gagal memuat daftar pengguna");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await api.post("/users", form);
      setUsers((prev) => [...prev, res.data]);
      toast.success("Pengguna berhasil ditambahkan");
      setOpen(false);
      setForm(EMPTY_FORM);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      toast.success("Pengguna berhasil dihapus");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <PageContainer
      title="Kelola User"
      subtitle="Tambah dan atur akun beserta perannya"
      action={
        <Button
          data-testid="btn-open-add-user"
          onClick={() => setOpen(true)}
          className="rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Tambah User
        </Button>
      }
    >
      <div className="bg-white rounded-2xl shadow-sm border border-[#E0A988]/40 p-6">
        {loading ? (
          <p className="text-sm text-[#C99A6A] py-6 text-center">Memuat...</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-[#F0E3D2]">
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow className="bg-[#FBF3E8]">
                  <TableHead className="text-xs font-semibold">Nama</TableHead>
                  <TableHead className="text-xs font-semibold">Username</TableHead>
                  <TableHead className="text-xs font-semibold">Role</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-[#7A4A28]">
                      {u.name}
                      {u.id === me.id && (
                        <span className="ml-2 text-[10px] text-[#A06E4D]">(Anda)</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#7A4A28]">{u.username}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${ROLE_BADGE[u.role] ?? ""} font-medium`}>
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {u.id !== me.id && (
                        <Button
                          data-testid={`btn-delete-user-${u.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(u.id)}
                          className="h-8 w-8 text-slate-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#8A5230] flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Tambah Pengguna Baru
            </DialogTitle>
            <DialogDescription className="text-[#A06E4D]">
              Buat akun baru dan tentukan perannya.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Nama</Label>
              <Input
                data-testid="input-user-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#FBF3E8] border-[#E0A988]/60"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Username</Label>
              <Input
                data-testid="input-user-username"
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="bg-[#FBF3E8] border-[#E0A988]/60"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Password</Label>
              <Input
                data-testid="input-user-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-[#FBF3E8] border-[#E0A988]/60"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#7A4A28]">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="select-user-role" className="bg-[#FBF3E8] border-[#E0A988]/60">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin — akses penuh</SelectItem>
                  <SelectItem value="pegawai" data-testid="role-option-pegawai">Pegawai — data sapi saja</SelectItem>
                  <SelectItem value="pengguna" data-testid="role-option-pengguna">Pengguna — akses dasar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="btn-submit-user"
              onClick={handleCreate}
              disabled={saving || !form.username || !form.name || !form.password}
              className="rounded-full bg-[#A06E4D] hover:bg-[#8A5230] text-white"
            >
              {saving ? "Menyimpan..." : "Simpan Pengguna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
